import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { autoDetectMeetingsPreferenceAtom } from '../components/pique/setting-modal/atoms';
import { enhancedNotify } from '../components/shared/notification-utils';
import { commands as notificationCommands } from '../tauri/plugins/notification';

export interface AutoDetectMeetingsPreference {
  enabled: boolean;
  isProcessing: boolean;
  toggle: (enable?: boolean) => Promise<void>;
}

export function useAutoDetectMeetingsPreference(): AutoDetectMeetingsPreference {
  const [autoDetect, setAutoDetect] = useAtom(autoDetectMeetingsPreferenceAtom);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!environment.isTauri) return;

    const syncTauriState = async () => {
      try {
        const detectEnabled = await notificationCommands.getDetectNotification();
        if (detectEnabled !== autoDetect) {
          setAutoDetect(detectEnabled);
        }
      } catch (error) {
        console.error('Failed to sync Tauri auto-detect state:', error);
      }
    };

    syncTauriState().catch(console.error);
  }, [autoDetect, setAutoDetect]);

  const toggle = useCallback(
    async (enable?: boolean) => {
      if (isProcessing || !environment.isTauri) return;

      const shouldEnable = enable !== undefined ? enable : !autoDetect;

      if (shouldEnable === autoDetect) return;

      setIsProcessing(true);

      try {
        if (shouldEnable) {
          await notificationCommands.setupAutoDetectPermissions();
          setAutoDetect(true);
        } else {
          await notificationCommands.stopDetectNotification();
          await notificationCommands.setDetectNotification(false);
          setAutoDetect(false);
        }
      } catch (error) {
        console.error('Failed to toggle auto-detect:', error);
        await enhancedNotify.error({
          title: 'Settings Update Failed',
          message:
            'Unable to update auto-detect preferences. Please try again.',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, autoDetect, setAutoDetect]
  );

  return {
    enabled: autoDetect,
    isProcessing,
    toggle,
  };
}
